/**
 * useAuth hook — subscribes to auth state changes.
 *
 * Usage:
 *   const { user, session, loading } = useAuth();
 *   if (loading) return <Spinner />;
 *   if (!user) return <Navigate to="/login" />;
 */

import { useEffect, useState } from "react";
import type { CarmenSession, CarmenUser } from "@carmen/auth-client";
import { carmenAuth } from "@/lib/carmenAuth";
import {
  getImpersonation,
  onImpersonationChange,
  type ImpersonationTarget,
} from "@/lib/impersonation";

type AccessRole = "superuser" | "hospital_admin" | "department_admin" | "association_admin" | "afdeling_secretary" | "it_admin" | "user";

const ROLE_LEVEL: Record<AccessRole, number> = {
  user: 10,
  it_admin: 12,
  afdeling_secretary: 15,
  association_admin: 20,
  department_admin: 30,
  hospital_admin: 40,
  superuser: 50,
};

function parseAccessRole(raw: unknown): AccessRole {
  if (typeof raw === "string" && raw in ROLE_LEVEL) return raw as AccessRole;
  return "user";
}

/**
 * The role the UI should act on. While impersonating, the backend swaps
 * the request identity to the target (auth.get_current_user), so the UI
 * follows the target's role too — that is what lets a superuser see
 * /admin exactly as a hospital_admin or secretary would.
 */
export function effectiveAccessRole(
  user: CarmenUser | null,
  impersonating: ImpersonationTarget | null,
): AccessRole {
  if (impersonating) return parseAccessRole(impersonating.access_role);
  return parseAccessRole((user?.app_metadata as Record<string, unknown> | undefined)?.access_role);
}

interface AuthState {
  user: CarmenUser | null;
  session: CarmenSession | null;
  loading: boolean;
  accessRole: AccessRole;
  impersonating: ImpersonationTarget | null;
}

type StoredAuthState = Omit<AuthState, "accessRole">;

export function useAuth(): AuthState {
  const [state, setState] = useState<StoredAuthState>({
    user: null,
    session: null,
    loading: true,
    impersonating: getImpersonation(),
  });

  useEffect(() => {
    let mounted = true;
    const unsubscribeImpersonation = onImpersonationChange(() => {
      if (!mounted) return;
      setState((prev) => ({ ...prev, impersonating: getImpersonation() }));
    });

    // The initial getSession() load and onAuthStateChange are both
    // in-flight from mount. If a state-change event (e.g. a sign-in
    // completing elsewhere) lands before the initial load's promise
    // settles, `initialSuperseded` stops the late initial-load result
    // from clobbering the newer state with stale data.
    let initialSuperseded = false;

    const applySession = (session: CarmenSession | null) => {
      if (!mounted) return;
      const u = session?.user ?? null;
      setState((prev) => ({
        user: u,
        session,
        loading: false,
        impersonating: prev.impersonating,
      }));
    };

    const { unsubscribe } = carmenAuth.onAuthStateChange((_event, carmenSession) => {
      initialSuperseded = true;
      applySession(carmenSession);
    });

    void carmenAuth
      .getSession()
      .then((carmenSession) => {
        if (initialSuperseded) return;
        applySession(carmenSession);
      })
      .catch(() => {
        // A rejected initial load (e.g. a near-expiry refresh against an
        // unreachable backend) must not leave `loading` stuck true.
        if (initialSuperseded) return;
        applySession(null);
      });

    return () => {
      mounted = false;
      unsubscribe();
      unsubscribeImpersonation();
    };

  }, []);

  return { ...state, accessRole: effectiveAccessRole(state.user, state.impersonating) };
}

export { ROLE_LEVEL };
export type { AccessRole };
